import type { AgendaDay, AgendaEvent, MemberIds, MemberView } from '@amumin/api-client';
import type { AppMessages } from '@amumin/i18n';
import { t } from '@amumin/i18n';
import { NEUTRAL_COLOR, type EventCardMember } from '@amumin/ui-core';

import { addDays, weekdayOf } from './dates.js';
import { memberInitials } from './initials.js';

// Pure hulpjes voor de agenda-schermen (DESIGN.md §8.W1/W2): van API-occurrences naar wat de
// kaarten en rasters tonen. Geen DOM, geen fetch: getest in agenda.test.ts.

export type MemberLite = Pick<MemberView, 'id' | 'display_name' | 'color_key' | 'avatar_icon'> &
  Partial<
    Pick<
      MemberView,
      'kind' | 'age_band' | 'version' | 'sort_order' | 'initials' | 'todo_assignable'
    >
  >;

/** To-do toewijzen (oprichter 2 okt): iedereen behalve baby's en peuters (de server beslist). */
export function todoAssignees<T extends { todo_assignable?: boolean }>(
  members: ReadonlyArray<T>,
): T[] {
  return members.filter((m) => m.todo_assignable !== false);
}

export function memberMap<T extends { id: string }>(members: ReadonlyArray<T>): Map<string, T> {
  return new Map(members.map((m) => [m.id, m]));
}

/** Leden van een item; 'everyone' = alle leden in volgorde van het huishouden. */
export function membersOf<T extends { id: string }>(
  ids: MemberIds,
  members: ReadonlyArray<T>,
): T[] {
  if (ids === 'everyone') return [...members];
  const byId = memberMap(members);
  const out: T[] = [];
  for (const id of ids) {
    const m = byId.get(id);
    if (m) out.push(m);
  }
  return out;
}

export function isEveryone(ids: MemberIds, members: ReadonlyArray<{ id: string }>): boolean {
  if (ids === 'everyone') return true;
  return members.length > 1 && members.every((m) => ids.includes(m.id));
}

/**
 * Kaartkleur (kleur = persoon): één of enkele leden → de kleur van het eerste lid (School van Sem en
 * Lotte blijft herkenbaar, geen rij grijze pillen); het hele gezin → rustig grijs (NEUTRAL_COLOR, los van de lidkleur 'black').
 */
export function colorKeyFor(ids: MemberIds, members: ReadonlyArray<MemberLite>): string {
  if (isEveryone(ids, members)) return NEUTRAL_COLOR;
  const first = membersOf(ids, members)[0];
  return first ? first.color_key : NEUTRAL_COLOR;
}

/**
 * Alle lidkleuren van een afspraak van een paar leden (oprichter 1 okt: een Sohbet van Baba én Anne
 * toont beide kleuren); leeg bij één lid of het hele gezin (dan geldt colorKeyFor).
 */
export function colorKeysFor(ids: MemberIds, members: ReadonlyArray<MemberLite>): string[] {
  if (isEveryone(ids, members)) return [];
  const out: string[] = [];
  for (const m of membersOf(ids, members)) if (out.indexOf(m.color_key) < 0) out.push(m.color_key);
  return out.length > 1 ? out : [];
}

/**
 * 0057 (oprichter 1 okt): de initialen van de leden achter de titel ("Sohbet · A", "B, A"); leeg
 * voor het hele gezin of zonder leden.
 */
export function initialsFor(ids: MemberIds, members: ReadonlyArray<MemberLite>): string {
  if (isEveryone(ids, members)) return '';
  return membersOf(ids, members)
    .map(memberInitials)
    .filter((x) => x !== '')
    .join(', ');
}

export function toCardMember(m: MemberLite): EventCardMember {
  return { name: m.display_name, colorKey: m.color_key, icon: m.avatar_icon };
}

/** `HH:MM` → minuten sinds middernacht; `24:00` (loopt door) = 1440. */
export function minutesOf(hhmm: string | null | undefined): number {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function hhmmOf(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
}

/** Rijder van de eerstvolgende rol: lid, 'missing' ("Wie rijdt?") of null (geen rollen). */
export function driverOf(
  ev: Pick<AgendaEvent, 'roles' | 'needs_driver'>,
  members: ReadonlyArray<MemberLite>,
): EventCardMember | 'missing' | null {
  if (ev.roles.length === 0) return null;
  // Een open rit is belangrijker dan een geregelde: dan staat er "Wie rijdt?".
  if (ev.needs_driver || ev.roles.some((r) => r.member_id === null)) return 'missing';
  const byId = memberMap(members);
  for (const role of ev.roles) {
    const m = role.member_id ? byId.get(role.member_id) : undefined;
    if (m) return toCardMember(m);
  }
  return null;
}

/**
 * "Anne brengt · Baba haalt" als brengen en halen elk een (andere) bekende rijder hebben; anders
 * null en blijft het "{name} rijdt" (release-c r2: in de weeklijst stond alleen wie brengt).
 */
export function splitDriversText(
  ev: Pick<AgendaEvent, 'roles'>,
  members: ReadonlyArray<MemberLite>,
  template: string,
): string | null {
  const drop = ev.roles.find((r) => r.role === 'dropoff');
  const pick = ev.roles.find((r) => r.role === 'pickup');
  if (!drop?.member_id || !pick?.member_id || drop.member_id === pick.member_id) return null;
  const byId = memberMap(members);
  const a = byId.get(drop.member_id);
  const b = byId.get(pick.member_id);
  if (!a || !b) return null;
  return template.replace('{dropoff}', a.display_name).replace('{pickup}', b.display_name);
}

/** Teksten voor brengen en halen in de agenda (design.eventCard + modules.rides). */
export interface RideWords {
  whoDrives: string;
  drives: string;
  splitDrives: string;
  travel: string;
  travelWho: string;
}

/**
 * 0029: de ritregel van één keer, zoals de lijst hem al toont (EventCard): "Wie rijdt?" (alert),
 * "Baba brengt · Anne haalt" als een ander haalt, anders "Baba rijdt". null = geen ritten.
 * Dag, week en maand gebruiken dezelfde regel, zodat elke weergave hetzelfde zegt.
 */
export function rideNote(
  ev: Pick<AgendaEvent, 'roles' | 'needs_driver'>,
  members: ReadonlyArray<MemberLite>,
  words: RideWords,
): { text: string; alert: boolean } | null {
  const driver = driverOf(ev, members);
  if (driver === null) return null;
  if (driver === 'missing') return { text: words.whoDrives, alert: true };
  const split = splitDriversText(ev, members, words.splitDrives);
  return { text: split ?? words.drives.replace('{name}', driver.name), alert: false };
}

/** 0029: gezichtjes bij de rit (wie brengt, dan wie haalt als dat een ander is). */
export function rideFaces(
  ev: Pick<AgendaEvent, 'roles'>,
  members: ReadonlyArray<MemberLite>,
): Array<{ name: string; colorKey: string; icon: string | null; initials: string }> {
  const byId = memberMap(members);
  const out: Array<{ name: string; colorKey: string; icon: string | null; initials: string }> = [];
  const seen = new Set<string>();
  for (const role of ['dropoff', 'pickup'] as const) {
    const id = ev.roles.find((r) => r.role === role)?.member_id;
    const m = id ? byId.get(id) : undefined;
    if (!m || seen.has(m.id)) continue;
    seen.add(m.id);
    out.push({
      name: m.display_name,
      colorKey: m.color_key,
      icon: m.avatar_icon ?? null,
      initials: memberInitials(m),
    });
  }
  return out;
}

/**
 * 0029: label van het reisblokje vóór de afspraak: "Baba rijdt · 15 min" (met de rijder van
 * brengen, als die er is en de ritten in de agenda staan), anders "15 min onderweg".
 */
export function travelLeadLabel(
  ev: Pick<AgendaEvent, 'roles' | 'travel'>,
  members: ReadonlyArray<MemberLite>,
  words: RideWords,
  withDriver: boolean,
): string | null {
  if (!ev.travel) return null;
  const minutes = String(ev.travel.minutes);
  const drop = withDriver ? ev.roles.find((r) => r.role === 'dropoff') : undefined;
  const who = drop && drop.member_id ? memberMap(members).get(drop.member_id) : undefined;
  return who
    ? words.travelWho.replace('{name}', who.display_name).replace('{minutes}', minutes)
    : words.travel.replace('{minutes}', minutes);
}

/**
 * Afgelaste keren tonen we doorgestreept van vandaag tot en met vandaag + 7 (DESIGN.md §8.W2,
 * ONTWERP CAL-40); daarbuiten en in het verleden laten we ze weg (rust).
 */
export function showCancelled(dayKey: string, todayKey: string): boolean {
  return dayKey >= todayKey && dayKey <= addDays(todayKey, 7);
}

/** Zichtbare afspraken van een dag, gefilterd op leden (lege set = iedereen). */
export function visibleEvents(
  day: Pick<AgendaDay, 'date' | 'events'>,
  todayKey: string,
  filter: ReadonlySet<string>,
): AgendaEvent[] {
  return day.events.filter((ev) => {
    if (ev.cancelled && !showCancelled(day.date, todayKey)) return false;
    if (filter.size === 0 || ev.member_ids === 'everyone') return true;
    return ev.member_ids.some((id) => filter.has(id));
  });
}

export type NextStatus = 'now' | 'next';

/** "Nu bezig" of "Straks" op vandaag (focuspunt van W1); hele-dag-items tellen niet mee. */
export function nextUp(
  events: ReadonlyArray<AgendaEvent>,
  nowMinutes: number,
): { event: AgendaEvent; status: NextStatus; minutes: number } | null {
  const timed = events
    .filter((ev) => !ev.all_day && !ev.cancelled && ev.start_hhmm)
    .sort((a, b) => minutesOf(a.start_hhmm) - minutesOf(b.start_hhmm));
  for (const ev of timed) {
    const start = ev.continues_from_previous ? 0 : minutesOf(ev.start_hhmm);
    const end = minutesOf(ev.end_hhmm ?? ev.start_hhmm);
    if (start <= nowMinutes && nowMinutes < end) {
      return { event: ev, status: 'now', minutes: end - nowMinutes };
    }
    if (start > nowMinutes) return { event: ev, status: 'next', minutes: start - nowMinutes };
  }
  return null;
}

/** "25 min", "1 uur 5 min", "2 uur". */
export function formatDuration(minutes: number, a: AppMessages): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return t(a.common.minutesShort, { count: total });
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (m === 0) return t(a.common.hours, { count: h });
  return t(a.common.hoursMinutes, { h, m });
}

/** Maandag van de week waarin `key` valt. */
export function mondayOf(key: string): string {
  return addDays(key, 1 - weekdayOf(key));
}

/** Golf 6b: ook de werkweek (maandag tot en met vrijdag). */
export type AgendaView = 'day' | 'workweek' | 'week' | 'month';

/** Te laden bereik per weergave (maand: volle weken ma–zo rond de maand). */
export function rangeFor(view: AgendaView, anchor: string): { from: string; to: string } {
  if (view === 'day') return { from: anchor, to: anchor };
  if (view === 'week') {
    const from = mondayOf(anchor);
    return { from, to: addDays(from, 6) };
  }
  if (view === 'workweek') {
    // Fixer golf 6b r2: op zaterdag of zondag de KOMENDE werkweek (op zondagavond plan je de week
    // die komt, niet de week die voorbij is).
    const from = addDays(mondayOf(anchor), weekdayOf(anchor) >= 6 ? 7 : 0);
    return { from, to: addDays(from, 4) };
  }
  const first = `${anchor.slice(0, 7)}-01`;
  const from = mondayOf(first);
  const [y, m] = first.split('-').map(Number) as [number, number];
  const nextMonth = m === 12 ? `${y + 1}-01-01` : `${y}-${m + 1 < 10 ? '0' : ''}${m + 1}-01`;
  const last = addDays(nextMonth, -1);
  return { from, to: addDays(mondayOf(last), 6) };
}

/** Volgende/vorige ankerdatum voor de pijltjes. */
export function shiftAnchor(view: AgendaView, anchor: string, dir: 1 | -1): string {
  if (view === 'day') return addDays(anchor, dir);
  if (view === 'week' || view === 'workweek') return addDays(anchor, 7 * dir);
  const [y, m] = anchor.split('-').map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + dir;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${nm < 10 ? '0' : ''}${nm}-01`;
}

export function greetingKey(
  hour: number,
): 'goodMorning' | 'goodAfternoon' | 'goodEvening' | 'goodNight' {
  if (hour < 6) return 'goodNight';
  if (hour < 12) return 'goodMorning';
  if (hour < 18) return 'goodAfternoon';
  return 'goodEvening';
}

/** Occurrence-id → originele lokale datumtijd (`<uuid>:YYYY-MM-DDTHH:MM:SS`). */
export function occurrenceDate(occurrenceId: string): string {
  const idx = occurrenceId.indexOf(':');
  return idx >= 0 ? occurrenceId.slice(idx + 1, idx + 11) : '';
}

/** Namen als "Sem", "Sem en Lotte", "Sem, Lotte en Noor". */
export function joinNames(names: ReadonlyArray<string>, and = 'en'): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1] ?? ''}`;
}

/** Eerste letter als hoofdletter (samenvattingen uit de API beginnen soms klein). */
export function capitalize(text: string | null | undefined): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Rijtijd vóór een brengen/halen zonder reistijd (zelfde als de API, present.ts). */
const DEFAULT_DRIVE_MIN = 15;

interface PersonInterval {
  from: number;
  to: number;
  /** Rit (brengen/halen) i.p.v. zelf aanwezig; `at` = vertrektijd. */
  drive: boolean;
  at: string | null;
}

function intervalsOf(
  ev: AgendaEvent,
  members: ReadonlyArray<{ id: string }>,
): Map<string, PersonInterval[]> {
  const out = new Map<string, PersonInterval[]>();
  if (ev.all_day || ev.cancelled) return out;
  const add = (id: string, iv: PersonInterval) => {
    const list = out.get(id);
    if (list) list.push(iv);
    else out.set(id, [iv]);
  };
  const from = Date.parse(ev.start_utc);
  const to = Date.parse(ev.end_utc);
  for (const m of membersOf(ev.member_ids, members))
    add(m.id, { from, to, drive: false, at: null });
  for (const r of ev.roles) {
    if (!r.member_id) continue;
    const at = Date.parse(r.at_utc);
    const minutes = ev.travel ? ev.travel.minutes : DEFAULT_DRIVE_MIN;
    const start = at - minutes * 60_000;
    const depart = hhmmOf(minutesOf(r.at_hhmm) - minutes);
    add(r.member_id, { from: start, to: at, drive: true, at: r.depart_hhmm ?? depart });
  }
  return out;
}

export interface ConflictPerson {
  memberId: string;
  /** Dit lid rijdt voor `ev` terwijl het nog bij de andere afspraak is. */
  driving: boolean;
  departHhmm: string | null;
}

/**
 * Wie botst er echt tussen `ev` en `other` (CAL-31/32)? Alleen leden waarvan een tijdvak in beide
 * overlapt: aanwezig zijn, of rijden (brengen/halen). "Papa, Mama, Sem, … heeft dan ook Avondeten"
 * terwijl alleen Papa's rit overlapt, is dus uitgesloten.
 */
export function conflictPeople(
  ev: AgendaEvent,
  other: AgendaEvent,
  members: ReadonlyArray<{ id: string }>,
): ConflictPerson[] {
  const mine = intervalsOf(ev, members);
  const theirs = intervalsOf(other, members);
  const out: ConflictPerson[] = [];
  for (const m of members) {
    const a = mine.get(m.id);
    const b = theirs.get(m.id);
    if (!a || !b) continue;
    let hit: PersonInterval | null = null;
    for (const x of a) {
      for (const y of b) if (x.from < y.to && y.from < x.to) hit = hit && !hit.drive ? hit : x;
    }
    if (hit) out.push({ memberId: m.id, driving: hit.drive, departHhmm: hit.at });
  }
  return out;
}
